import type { AgentHop } from './ConverseModel';

/**
 * Cliente do canal `GET /api/v1/agents/conversation` (T25, 29/08).
 *
 * A conversa nasce com canal aberto para ficar fluida quando o agente virar LLM e
 * a resposta chegar em pedaços. Hoje a chamada é atômica — os saltos chegam todos
 * de uma vez —, mas o **formato** já é o de streaming: uma mensagem por evento,
 * e a tela cresce a troca a cada uma.
 *
 * O canal não substitui as rotas REST: elas continuam sendo a alternativa quando
 * o upgrade falha (proxy corporativo que não repassa `Upgrade`). Quem escolhe é
 * `useConversa`.
 */

/** Eventos do servidor (`api-backend.md` › WebSocket /api/v1/agents/conversation). */
export type ChannelEvent =
  | ({ kind: 'salto' } & AgentHop)
  | {
      kind: 'resposta';
      agent: string;
      delegated_to: string;
      person: string;
      tool: string;
      http_status: number;
      body: unknown;
      request_id: string;
      duration_ms: number;
    }
  | {
      kind: 'decisao';
      verdict: string;
      /** O `request_id` **da chamada** — vem sempre, e é o que casa com a troca. */
      request_id: string;
      /**
       * O do log do gateway; vazio quando nada foi achado. É por ele que a tela
       * abre a linha certa em T17 — não há link montado pelo servidor, porque
       * `/decisoes` é rota única e a linha se abre por estado de rota.
       */
      decision_request_id: string;
    }
  | { kind: 'erro'; code: string; message: string }
  | { kind: 'ping' };

/** Pedidos do cliente. */
export type ChannelRequest =
  | { kind: 'enviar'; agent: string; person: string; tool: string }
  | { kind: 'delegar'; generalist: string; agent: string; person: string; tool: string }
  | { kind: 'pong' };

export const CHANNEL_PATH = '/api/v1/agents/conversation';

/**
 * URL do canal, derivada da origem da página.
 *
 * `http` → `ws` e `https` → `wss` pela mesma origem: o cookie de sessão é
 * `httpOnly` e same-site, então o canal **precisa** sair da mesma origem para
 * levá-lo — é o que autentica o upgrade. `VITE_API_URL` (quando existe) manda,
 * como no cliente HTTP.
 */
export function channelURL(base = import.meta.env.VITE_API_URL ?? ''): string {
  const origem = base ? new URL(base, window.location.href) : new URL(window.location.href);
  const protocolo = origem.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocolo}//${origem.host}${CHANNEL_PATH}`;
}

export interface OpenChannel {
  enviar: (pedido: ChannelRequest) => void;
  fechar: () => void;
}

export interface ChannelOptions {
  onAbrir: () => void;
  onEvento: (event: ChannelEvent) => void;
  /** Chamado quando o canal fecha ou nunca abriu. `abriu` distingue os dois. */
  onFechar: (abriu: boolean) => void;
  /** Injetável nos testes; padrão é o `WebSocket` do navegador. */
  criar?: (url: string) => WebSocket;
}

/**
 * Abre o canal. Nunca lança: falha de conexão chega por `onFechar(false)`, que é
 * o sinal para cair no REST.
 */
export function openChannel(opcoes: ChannelOptions): OpenChannel {
  const create = opcoes.criar ?? ((url: string) => new WebSocket(url));
  let abriu = false;
  let closedByIn = false;

  let socket: WebSocket;
  try {
    socket = create(channelURL());
  } catch {
    // Construtor que lança (URL inválida, ambiente sem WebSocket) é o mesmo caso
    // de canal que não abre — quem chama não precisa distinguir.
    opcoes.onFechar(false);
    return { enviar: () => {}, fechar: () => {} };
  }

  socket.onopen = () => {
    abriu = true;
    opcoes.onAbrir();
  };

  socket.onmessage = (mensagem: MessageEvent) => {
    let event: ChannelEvent;
    try {
      event = JSON.parse(String(mensagem.data)) as ChannelEvent;
    } catch {
      // Quadro que não é JSON é ruído de intermediário, não evento: ignorar é
      // melhor do que derrubar a conversa por causa dele.
      return;
    }
    // O batimento se responde aqui e não sobe: para a tela ele não é um evento,
    // é o que impede o proxy de fechar o canal durante os minutos em que a
    // decisão atravessa o Log Analytics.
    if (event.kind === 'ping') {
      send({ kind: 'pong' });
      return;
    }
    opcoes.onEvento(event);
  };

  socket.onerror = () => {
    // `onerror` sempre vem seguido de `onclose`; quem avisa é ele, uma vez só.
  };

  socket.onclose = () => {
    if (!closedByIn) opcoes.onFechar(abriu);
  };

  function send(pedido: ChannelRequest) {
    if (socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify(pedido));
  }

  return {
    enviar: send,
    fechar: () => {
      closedByIn = true;
      socket.close();
    },
  };
}
